/**
 * LifecycleStructureTree — panel izquierdo del Lifecycle Designer.
 * (B.6.d.9.d + B.6.d.9.i — modelo "deliverable" 1:1 sub-fase ↔ artifact)
 *
 * Lista plana de phases en orden. Cada phase es expandible y muestra
 * sus sub-fases (cada sub-fase es un entregable: 1:1 con un artifact
 * declarado en `expected_artifacts[]`).
 *
 * Cada fila de sub-fase muestra:
 *   <subphase_code> · <subphase_name> → <template_key>  [badges]
 *
 * Acciones inline: ↑/↓ (mover), 🗑 (eliminar entregable completo).
 *
 * El click "+ Añadir sub-fase" abre el modal picker (manejado por el
 * orquestador via `onAddDeliverableRequest`).
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Infinity as InfinityIcon,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react'

import { listTemplates, type LifecycleContractMode } from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import {
  findPhaseI18n,
  findSubphaseI18n,
  type LifecycleDesignerAction,
  type LifecycleDesignerState,
} from './lifecycleState'

interface LifecycleStructureTreeProps {
  state: LifecycleDesignerState
  dispatch: React.Dispatch<LifecycleDesignerAction>
  readOnly: boolean
  /** Se invoca cuando el usuario clickea "+ Añadir sub-fase" en una
   * phase. El parent monta el modal picker. */
  onAddDeliverableRequest: (phaseCode: string) => void
}

const CONTRACT_MODE_BADGE: Record<
  LifecycleContractMode,
  { label: string; className: string }
> = {
  internal: {
    label: 'INTERNAL',
    className: 'bg-brand-soft text-brand',
  },
  skipped: {
    label: 'SKIPPED',
    className: 'bg-surface-muted text-ink-subtle',
  },
}

export function LifecycleStructureTree({
  state,
  dispatch,
  readOnly,
  onAddDeliverableRequest,
}: LifecycleStructureTreeProps) {
  const { t } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set())

  // Catálogo de roles para resolver template_key → name del rol en los
  // badges de cada entregable.
  const { data: roles = [] } = useQuery({
    queryKey: ['templates', 'list', 'role'],
    queryFn: () =>
      listTemplates(accessToken!, { template_type: 'role' }),
    enabled: Boolean(accessToken),
    staleTime: 60_000,
  })
  const roleNameByKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of roles) map.set(r.template_key, r.name)
    return map
  }, [roles])

  function togglePhase(phaseCode: string) {
    setCollapsedPhases((prev) => {
      const next = new Set(prev)
      if (next.has(phaseCode)) next.delete(phaseCode)
      else next.add(phaseCode)
      return next
    })
  }

  const phases = [...state.definition.phases].sort(
    (a, b) => a.phase_order - b.phase_order,
  )

  const totalDeliverables = phases.reduce(
    (acc, p) => acc + (p.subphases?.length ?? 0),
    0,
  )

  return (
    <aside className="flex flex-col h-full bg-surface border-r border-line min-h-0">
      <header className="px-4 py-3 border-b border-line flex items-baseline justify-between">
        <h2 className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
          {t('designer.lifecycle.tree_title')}
        </h2>
        <span className="text-[10px] text-ink-subtle">
          {t('designer.lifecycle.tree_count_deliverables', {
            phases: phases.length,
            deliverables: totalDeliverables,
          })}
        </span>
      </header>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {phases.length === 0 ? (
          <p className="px-3 py-6 font-body text-xs text-ink-subtle text-center">
            {t('designer.lifecycle.tree_empty_hint')}
          </p>
        ) : (
          phases.map((phase, idx) => {
            const collapsed = collapsedPhases.has(phase.phase_code)
            const isFirst = idx === 0
            const isLast = idx === phases.length - 1
            const i18n = findPhaseI18n(
              state.definition,
              phase.phase_code,
              state.activeLocale,
            )
            const phaseSelected =
              state.selectedNode?.kind === 'phase' &&
              state.selectedNode.phaseCode === phase.phase_code
            const modeBadge =
              CONTRACT_MODE_BADGE[phase.default_contract_mode] ??
              CONTRACT_MODE_BADGE.internal
            const overridable = phase.contract_mode_overridable === true
            return (
              <div key={phase.phase_code} className="space-y-0.5">
                {/* Phase header row */}
                <PhaseRow
                  phaseNumber={idx}
                  phaseTitle={i18n?.name ?? phase.phase_code}
                  phaseCode={phase.phase_code}
                  collapsed={collapsed}
                  selected={phaseSelected}
                  modeBadge={modeBadge}
                  overridable={overridable}
                  isFirst={isFirst}
                  isLast={isLast}
                  readOnly={readOnly}
                  onToggle={() => togglePhase(phase.phase_code)}
                  onSelect={() =>
                    dispatch({
                      type: 'SELECT_NODE',
                      node: { kind: 'phase', phaseCode: phase.phase_code },
                    })
                  }
                  onMove={(direction) =>
                    dispatch({
                      type: 'MOVE_PHASE',
                      phaseCode: phase.phase_code,
                      direction,
                    })
                  }
                  onRemove={() =>
                    dispatch({
                      type: 'REMOVE_PHASE',
                      phaseCode: phase.phase_code,
                    })
                  }
                />

                {/* Phase body — sub-fases (entregables) cuando expandido */}
                {!collapsed ? (
                  <div className="ml-4 border-l border-line pl-3 py-1">
                    <DeliverableSection
                      state={state}
                      dispatch={dispatch}
                      readOnly={readOnly}
                      phaseCode={phase.phase_code}
                      roleNameByKey={roleNameByKey}
                      onAddDeliverableRequest={onAddDeliverableRequest}
                    />
                  </div>
                ) : null}
              </div>
            )
          })
        )}
      </div>

      {!readOnly ? (
        <div className="border-t border-line p-2">
          <button
            type="button"
            onClick={() => dispatch({ type: 'ADD_PHASE' })}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-line py-2 text-xs text-ink-muted hover:text-ink hover:border-line-strong transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('designer.lifecycle.add_phase')}
          </button>
        </div>
      ) : null}

      {/* Stats colapsable */}
      <Stats
        phases={phases.length}
        deliverables={totalDeliverables}
        contractModes={phases.map((p) => p.default_contract_mode)}
      />
    </aside>
  )
}

// ─── Phase row (header) ────────────────────────────────────────────────────
function PhaseRow({
  phaseNumber,
  phaseTitle,
  phaseCode,
  collapsed,
  selected,
  modeBadge,
  overridable,
  isFirst,
  isLast,
  readOnly,
  onToggle,
  onSelect,
  onMove,
  onRemove,
}: {
  phaseNumber: number
  phaseTitle: string
  phaseCode: string
  collapsed: boolean
  selected: boolean
  modeBadge: { label: string; className: string }
  overridable: boolean
  isFirst: boolean
  isLast: boolean
  readOnly: boolean
  onToggle: () => void
  onSelect: () => void
  onMove: (direction: -1 | 1) => void
  onRemove: () => void
}) {
  const { t } = useTranslation('templates')
  const containerClass = selected
    ? 'group rounded-md border border-brand bg-brand-soft/40 px-2 py-1.5 cursor-pointer'
    : 'group rounded-md border border-transparent hover:bg-surface-muted px-2 py-1.5 cursor-pointer'
  return (
    <div className={containerClass} onClick={onSelect}>
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
          aria-label={
            collapsed
              ? t('designer.lifecycle.phase_expand')
              : t('designer.lifecycle.phase_collapse')
          }
          className="p-0.5 rounded text-ink-muted hover:text-ink shrink-0"
        >
          {collapsed ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </button>
        <span className="font-mono text-[10px] text-ink-subtle tabular-nums shrink-0 w-5 text-right">
          {phaseNumber}.
        </span>
        <span className="flex-1 truncate text-sm text-ink" title={phaseCode}>
          {phaseTitle}
        </span>
        {!readOnly ? (
          <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onMove(-1)
              }}
              disabled={isFirst}
              aria-label={t('designer.lifecycle.move_up')}
              className="p-0.5 rounded text-ink-muted hover:text-ink hover:bg-surface-muted disabled:opacity-30"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onMove(1)
              }}
              disabled={isLast}
              aria-label={t('designer.lifecycle.move_down')}
              className="p-0.5 rounded text-ink-muted hover:text-ink hover:bg-surface-muted disabled:opacity-30"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              aria-label={t('designer.lifecycle.remove_phase')}
              className="p-0.5 rounded text-ink-muted hover:text-critical hover:bg-critical-soft"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </span>
        ) : null}
      </div>
      <div className="mt-0.5 ml-9 flex items-center gap-1 text-[9px]">
        <span
          className={`font-mono uppercase tracking-wider px-1 rounded ${modeBadge.className}`}
        >
          {modeBadge.label}
        </span>
        {overridable ? (
          <span
            className="font-mono uppercase tracking-wider px-1 rounded bg-official-soft text-official"
            title={t('designer.lifecycle.contract_overridable_hint')}
          >
            user-override
          </span>
        ) : null}
      </div>
    </div>
  )
}

// ─── Deliverable section (sub-fases 1:1 con artifacts) ────────────────────
function DeliverableSection({
  state,
  dispatch,
  readOnly,
  phaseCode,
  roleNameByKey,
  onAddDeliverableRequest,
}: {
  state: LifecycleDesignerState
  dispatch: React.Dispatch<LifecycleDesignerAction>
  readOnly: boolean
  phaseCode: string
  roleNameByKey: Map<string, string>
  onAddDeliverableRequest: (phaseCode: string) => void
}) {
  const { t } = useTranslation('templates')
  const phase = state.definition.phases.find((p) => p.phase_code === phaseCode)
  const phaseMode = phase?.default_contract_mode ?? 'internal'
  const isSkipped = phaseMode === 'skipped'
  const subphases = [...(phase?.subphases ?? [])].sort(
    (a, b) => a.subphase_order - b.subphase_order,
  )
  const artifactsBySubphase = new Map(
    (phase?.expected_artifacts ?? []).map((a) => [a.subphase_code, a]),
  )

  return (
    <section>
      <p className="text-[9px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
        {t('designer.lifecycle.subphases_section', { count: subphases.length })}
      </p>
      {isSkipped ? (
        <p className="font-body text-[11px] text-ink-subtle italic mb-1">
          {t('designer.lifecycle.tree_skipped_hint')}
        </p>
      ) : subphases.length === 0 ? (
        <p className="font-body text-[11px] text-ink-subtle italic mb-1">—</p>
      ) : (
        <ul className="space-y-0.5">
          {subphases.map((sp, idx) => {
            const i18n = findSubphaseI18n(
              state.definition,
              phaseCode,
              sp.subphase_code,
              state.activeLocale,
            )
            const art = artifactsBySubphase.get(sp.subphase_code)
            const selected =
              state.selectedNode?.kind === 'subphase' &&
              state.selectedNode.phaseCode === phaseCode &&
              state.selectedNode.subphaseCode === sp.subphase_code
            const isFirst = idx === 0
            const isLast = idx === subphases.length - 1
            return (
              <li
                key={sp.subphase_code}
                className={
                  selected
                    ? 'group flex flex-col rounded border border-brand bg-brand-soft/40 px-1.5 py-1 text-xs cursor-pointer'
                    : 'group flex flex-col rounded border border-transparent hover:bg-surface-muted px-1.5 py-1 text-xs cursor-pointer'
                }
                onClick={() =>
                  dispatch({
                    type: 'SELECT_NODE',
                    node: {
                      kind: 'subphase',
                      phaseCode,
                      subphaseCode: sp.subphase_code,
                    },
                  })
                }
              >
                <div className="flex items-center gap-1 min-w-0">
                  <span className="font-mono text-[10px] text-ink-subtle tabular-nums shrink-0">
                    {sp.subphase_code}
                  </span>
                  <span className="flex-1 truncate text-ink">
                    {i18n?.name ?? sp.subphase_code}
                  </span>
                  {art?.user_can_add_more ? (
                    <InfinityIcon
                      className="w-3 h-3 text-official shrink-0"
                      aria-label={t(
                        'designer.lifecycle.artifact_user_can_add_more_label',
                      )}
                    />
                  ) : null}
                  {art?.is_optional ? (
                    <span className="font-mono text-[9px] uppercase tracking-wider px-1 rounded bg-surface-muted text-ink-subtle shrink-0">
                      opc
                    </span>
                  ) : null}
                  {!readOnly ? (
                    <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          dispatch({
                            type: 'MOVE_SUBPHASE',
                            phaseCode,
                            subphaseCode: sp.subphase_code,
                            direction: -1,
                          })
                        }}
                        disabled={isFirst}
                        aria-label={t('designer.lifecycle.move_up')}
                        className="p-0.5 rounded text-ink-muted hover:text-ink hover:bg-surface-muted disabled:opacity-30"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          dispatch({
                            type: 'MOVE_SUBPHASE',
                            phaseCode,
                            subphaseCode: sp.subphase_code,
                            direction: 1,
                          })
                        }}
                        disabled={isLast}
                        aria-label={t('designer.lifecycle.move_down')}
                        className="p-0.5 rounded text-ink-muted hover:text-ink hover:bg-surface-muted disabled:opacity-30"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          dispatch({
                            type: 'REMOVE_DELIVERABLE',
                            phaseCode,
                            subphaseCode: sp.subphase_code,
                          })
                        }}
                        aria-label={t('designer.lifecycle.remove_deliverable')}
                        className="p-0.5 rounded text-ink-muted hover:text-critical hover:bg-critical-soft"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null}
                </div>
                {/* Línea 2 — template / carga externa + rol */}
                {art ? (
                  <div className="ml-4 mt-0.5 flex items-center gap-1 text-[10px] text-ink-muted flex-wrap">
                    <span className="text-ink-subtle">→</span>
                    {art.template_key ? (
                      <span className="font-mono truncate">
                        {art.template_key}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 italic text-ink-muted">
                        <Upload className="w-3 h-3" />
                        {t(
                          'designer.lifecycle.deliverable_external_kind_label',
                        )}
                      </span>
                    )}
                    {(art.default_count ?? 1) > 1 ? (
                      <span className="font-mono text-ink-subtle">
                        ×{art.default_count}
                      </span>
                    ) : null}
                    {art.role_template_key ? (
                      <span
                        className="inline-flex items-center font-mono text-[9px] uppercase tracking-wider px-1 rounded bg-official-soft/60 text-official"
                        title={t(
                          'designer.lifecycle.deliverable_role_label',
                        )}
                      >
                        {roleNameByKey.get(art.role_template_key) ??
                          art.role_template_key}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="ml-4 mt-0.5 text-[10px] text-critical italic">
                    {t('designer.lifecycle.deliverable_no_artifact')}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {!readOnly && !isSkipped ? (
        <button
          type="button"
          onClick={() => onAddDeliverableRequest(phaseCode)}
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand hover:underline"
        >
          <Plus className="w-3 h-3" />
          {t('designer.lifecycle.add_subphase')}
        </button>
      ) : null}
    </section>
  )
}

// ─── Stats footer (colapsable) ────────────────────────────────────────────
function Stats({
  phases,
  deliverables,
  contractModes,
}: {
  phases: number
  deliverables: number
  contractModes: LifecycleContractMode[]
}) {
  const { t } = useTranslation('templates')
  const internal = contractModes.filter((m) => m === 'internal').length
  const skipped = contractModes.filter((m) => m === 'skipped').length
  return (
    <details className="border-t border-line text-[10px] text-ink-subtle">
      <summary className="px-3 py-2 cursor-pointer hover:text-ink-muted font-medium uppercase tracking-wider">
        {t('designer.lifecycle.stats_title')}
      </summary>
      <dl className="px-3 pb-3 space-y-1.5">
        <StatRow label={t('designer.lifecycle.stats_phases')} value={phases} />
        <StatRow
          label={t('designer.lifecycle.stats_deliverables')}
          value={deliverables}
        />
        <div className="pt-1.5 border-t border-line space-y-1">
          <StatRow
            label={t('designer.lifecycle.stats_internal')}
            value={internal}
          />
          <StatRow
            label={t('designer.lifecycle.stats_skipped')}
            value={skipped}
          />
        </div>
      </dl>
    </details>
  )
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="font-mono text-ink tabular-nums">{value}</dd>
    </div>
  )
}
